import { connect, FormikValues } from 'formik'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Select } from '@/components/Form'
import { FormLabel, Row } from '@/components/StyledComponents'
import {
  StyledDateTimePicker,
  StyledRadio,
  StyledRadioInput
} from '@/pages/SpaceAppBuilder/components/StyledComponents'

import { ScheduledIntervalType } from './interfaces'
import messages from './messages'
import { getDaysOfWeekNumeric, getMonthsOfYear, getWeekOfMonth } from './utils'

const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;

  && {
    > * {
      margin-right: 10px;
    }
  }
`

const StyledSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  && {
    flex-basis: 40%;
    margin-right: 5px !important;
    .field__wrapper .field__input {
      color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
    }
  }
`

const StyledMonthlyRadio = styled(StyledRadio)`
  flex-direction: column;
  justify-content: center;
`

const Radio = styled(StyledRadioInput)`
  && {
    .field__input {
      margin-top: 30px;
    }
  }
`

const DateTimePicker = styled(StyledDateTimePicker)`
  .react-datepicker-wrapper {
    width: 123px;
  }
`

const StyledSpan = styled.span`
  color: #ff0000;
  margin-right: 5px;
`

function Yearly({ formik, readOnly }: ScheduledIntervalType & FormikValues) {
  const intl = useIntl()

  const { values, setFieldValue } = formik

  return (
    <>
      <StyledMonthlyRadio name="triggerData.isVerbose">
        <StyledRow>
          <Radio
            disabled={readOnly}
            id={`triggerData.isVerbose-0`}
            name="triggerData.isVerbose"
            value="false"
            labelLocation="after"
          />
          <div>
            <FormLabel>
              <StyledSpan>{intl.formatMessage(messages.required)}</StyledSpan>
              {intl.formatMessage(messages.every)}
            </FormLabel>
            <DateTimePicker
              readOnly={readOnly}
              showIcon={true}
              name="triggerData.dayMonth"
              showTimeSelect={false}
              dateFormat="MM/dd"
              placeholderText="__/__"
              value={values.triggerData.dayMonth}
              onChange={setFieldValue}
            />
          </div>
        </StyledRow>
        <StyledRow>
          <Radio
            disabled={readOnly}
            id={`triggerData.isVerbose-1`}
            data-testid={'verbose-radio'}
            name="triggerData.isVerbose"
            value="true"
            labelLocation="after"
          />
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.weekOfMonth`}
            isPlaceholder={!values.triggerData.weekOfMonth || values.triggerData.weekOfMonth < 0}
            label={intl.formatMessage(messages.onThe)}
            variant={''}
            defaultValue={-1}
          >
            <option value={-1} disabled>
              {intl.formatMessage(messages.pleaseSelectWeek)}
            </option>
            {getWeekOfMonth(intl).map((wom: any) => (
              <Select.Option key={wom.value} value={wom.value}>
                {wom.label}
              </Select.Option>
            ))}
          </StyledSelect>
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.dayOfWeek`}
            label="&nbsp;"
            isPlaceholder={!values.triggerData.dayOfWeek || values.triggerData.dayOfWeek < 0}
            variant={''}
            defaultValue={-1}
          >
            <option value={-1} disabled>
              {intl.formatMessage(messages.pleaseSelectDay)}
            </option>
            {getDaysOfWeekNumeric(intl).map((dow: any) => (
              <Select.Option key={dow.value} value={dow.value}>
                {dow.label}
              </Select.Option>
            ))}
          </StyledSelect>
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.monthOfYear`}
            isPlaceholder={!values.triggerData.monthOfYear || values.triggerData.monthOfYear < 0}
            label={intl.formatMessage(messages.in)}
            variant={''}
            defaultValue={-1}
          >
            <option value={-1} disabled>
              {intl.formatMessage(messages.pleaseSelectMonth)}
            </option>
            {getMonthsOfYear(intl).map((moy: any) => (
              <Select.Option key={moy.value} value={moy.value}>
                {moy.label}
              </Select.Option>
            ))}
          </StyledSelect>
        </StyledRow>
      </StyledMonthlyRadio>

      <FormLabel>{intl.formatMessage(messages.at)}</FormLabel>
      <StyledDateTimePicker
        readOnly={readOnly}
        name="triggerData.time"
        showTimeOnly={true}
        dateFormat="h:mm aa"
        placeholderText={intl.formatMessage(messages.pleaseSelectTime)}
        value={values.triggerData.time}
        onChange={setFieldValue}
      />
    </>
  )
}

export default connect(Yearly)
